import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { ExternalSource, OriginPerson } from "../../../utils/models";
import { integratorLabel } from "../../team/teamFilterOptions";
import type { OriginCopyContext } from "./requestOriginCopy";

/** The language, the department's timezone and its job and HR-system names, for the pure copy in `requestOriginCopy`. */
export const useOriginCopyContext = (): OriginCopyContext => {
	const { t, i18n } = useTranslation();
	const { timezone, chosenDepartment, isRealDemo, getNurseById, getNurseFormattedName } = useHospitalData();
	return useMemo<OriginCopyContext>(() => {
		const jobs = chosenDepartment.jobs;
		return {
			t,
			locale: i18n.language,
			timezone,
			jobNameOf: (jobId: string) => jobs?.find((job) => job.id === jobId)?.displayName ?? null,
			hrisLabelOf: (source: ExternalSource) => integratorLabel(source, t, isRealDemo),
			// The backend composes `name` in one fixed order; a roster nurse is re-named in the department's order.
			personName: (person: OriginPerson) => (person.nurseId !== null && getNurseById(person.nurseId) ? getNurseFormattedName(person.nurseId) : person.name),
		};
	}, [t, i18n.language, timezone, chosenDepartment.jobs, isRealDemo, getNurseById, getNurseFormattedName]);
};
